import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getProjectEstimates } from "Api/billingSystem/costs/getProjectEstimate.request";

import type { EstimationProject } from "Api/billingSystem/types";
import type { RootState } from "Store/configureStore";

// Thunk
export const getProjectEstimatesThunk = createAppAsyncThunk(
  "billingSystem/projectEstimate/get",
  async (
    {
      billingProfileId,
      projectId
    }: {
      billingProfileId: string;
      projectId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const projectEstimates = await getProjectEstimates({
        billingProfileId,
        projectId
      });
      return { projectId, projectEstimates };
    } catch (err) {
      logger(err, {
        action: "get_project_estimates",
        billingProfileId,
        projectId
      });
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

type EstimateData = {
  projectEstimates?: EstimationProject;
  loading: boolean;
  error?: string;
};
// State type
type ProjectEstimateState = {
  dataByProjectId: {
    [projectId: string]: EstimateData;
  };
};

const initialState: ProjectEstimateState = {
  dataByProjectId: {}
};

// Slice
const projectEstimateSlice = createSlice({
  name: "projectEstimate",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getProjectEstimatesThunk.pending, (state, { meta }) => {
        const { projectId } = meta.arg;

        setDeep(state, ["dataByProjectId", projectId, "loading"], true);
        setDeep(state, ["dataByProjectId", projectId, "error"], undefined);
      })
      .addCase(getProjectEstimatesThunk.fulfilled, (state, { payload }) => {
        const { projectId, projectEstimates } = payload;
        setDeep(state, ["dataByProjectId", projectId, "loading"], false);
        setDeep(
          state,
          ["dataByProjectId", projectId, "projectEstimates"],
          projectEstimates
        );
      })
      .addCase(
        getProjectEstimatesThunk.rejected,
        (state, { meta, payload }) => {
          const { projectId } = meta.arg;
          setDeep(state, ["dataByProjectId", projectId, "loading"], false);
          setDeep(
            state,
            ["dataByProjectId", projectId, "error"],
            payload?.error
          );
        }
      );
  }
});

export default projectEstimateSlice.reducer;

// Selectors
const projectEstimateStateSelector = (state: RootState) =>
  state.projectEstimate;

const selectProjectEstimateData = createSelector(
  projectEstimateStateSelector,
  (_: RootState, projectId: string) => projectId,
  (state, projectId) => state.dataByProjectId[projectId]
);

export const selectProjectEstimates = createSelector(
  selectProjectEstimateData,
  data => data?.projectEstimates
);

export const selectProjectEstimatesLoading = createSelector(
  selectProjectEstimateData,
  data => data?.loading ?? false
);

export const selectProjectEstimatesError = createSelector(
  selectProjectEstimateData,
  data => data?.error
);
